import { Dispatch, MouseEvent, SetStateAction, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowDropDownOutlined,
  ArticleOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  Menu as MenuIcon,
  NotificationsOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import FlexBetween from "@shared/components/FlexBetween";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { useNavigate } from "react-router-dom";
import { changeMode } from "@store/slices/themeSlice";
import { clearAll, logout } from "@store/slices/authSlice";
import Tooltip from "@mui/material/Tooltip";
import { saveAllDirtyResources } from "@store/thunks/workspaceThunks";

interface Props {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isNonMobile: boolean;
}

export const AdminNavbar = ({ isSidebarOpen, setIsSidebarOpen }: Props) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const themeState = useAppSelector((state) => state.theme);
  const userInfo = useAppSelector((state) => state.auth.userInfo);

  // Mirrors ArgNavbar: present the account control as the user's avatar
  // (photo, else initials, else a person glyph) rather than a generic icon.
  const accountName = userInfo?.full_name || userInfo?.username || null;
  const accountInitials = (() => {
    const src = (userInfo?.full_name || userInfo?.username || "").trim();
    if (!src) return null;
    const parts = src.split(/\s+/).filter(Boolean);
    const first = parts[0] ?? "";
    const last = parts.length >= 2 ? parts[parts.length - 1] ?? "" : "";
    const letters = last ? first.charAt(0) + last.charAt(0) : src.slice(0, 2);
    return letters.toUpperCase();
  })();

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    // Fire the save in the background. Blocking the logout on a
    // potentially slow or 401-ing save lets a stale "finally" fire
    // after the user has signed back in, clobbering the new session
    // — best-effort save is the right trade. At worst the user loses
    // a few seconds of edits the auto-save interval hadn't picked up.
    dispatch(saveAllDirtyResources())
      .unwrap()
      .catch((error) => {
        console.error("Error saving data on logout:", error);
      });
    dispatch(clearAll());
    dispatch(logout());
    navigate("/");
  };

  const handleNavigateHome = () => {
    navigate("/annogram");
  };

  return (
    <AppBar sx={{ position: "static", background: "none", boxShadow: "none" }}>
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Left Side */}
        <FlexBetween>
          <Tooltip title={isSidebarOpen ? "Close" : "Open"}>
            <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </FlexBetween>
        {/* Right Side */}
        <FlexBetween gap="1.5rem">
          <Tooltip
            title={themeState.mode === "dark" ? "Toggle Light" : "Toggle Dark"}
          >
            <IconButton onClick={() => dispatch(changeMode())}>
              {theme.palette.mode === "dark" ? (
                <DarkModeOutlined />
              ) : (
                <LightModeOutlined />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="No Notifications">
            <IconButton>
              <NotificationsOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reader/Canvas">
            <IconButton onClick={handleNavigateHome}>
              <ArticleOutlined />
            </IconButton>
          </Tooltip>
          <FlexBetween>
            <Tooltip title="Account">
              <Button
                onClick={handleClick}
                sx={{ minWidth: 0, gap: 0, px: 1, color: "inherit" }}
              >
                <Avatar
                  src={userInfo?.avatar_url ?? undefined}
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: "0.85rem",
                    bgcolor: "transparent",
                    color: "text.primary",
                  }}
                >
                  {accountInitials || <PersonOutlined sx={{ fontSize: 24 }} />}
                </Avatar>
                <ArrowDropDownOutlined sx={{ fontSize: "25px", ml: -0.75 }} />
              </Button>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Box sx={{ px: 2, py: 1, maxWidth: 260 }}>
                <Typography variant="subtitle2" noWrap>
                  {accountName || "Account"}
                </Typography>
                {userInfo?.email && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block" }}
                  >
                    {userInfo.email}
                  </Typography>
                )}
              </Box>
              <Divider />
              {/* Profile lives in the admin sidebar; keep this menu to
                  account identity + sign-out to avoid duplicating it. */}
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>
    </AppBar>
  );
};
